import React from 'react'
import {
  AdCreative,
  AdCreativeType,
  AdImage,
  AdVideo,
  CreativeMediaType,
  PlacementBucket,
} from '@marketing-milk/interfaces'
import { formatCallToAction } from './formatCallToAction'
import { videoStillURL } from '../util'

/**
 * The subset of `AdCreative` this presentational component actually reads. Kept
 * narrow so callers that don't have a full persisted creative (e.g. the creative
 * -import Review step, which only has in-flight edit state) can build a type-safe
 * object without casting. A full `AdCreative` stays assignable.
 */
export type LocalAdPreviewCreative = Pick<
  AdCreative,
  | 'format'
  | 'primaryText'
  | 'singleImage'
  | 'imageURL'
  | 'thumbnailURL'
  | 'linkDisplayURL'
  | 'linkHeadline'
  | 'linkDescription'
  | 'callToAction'
> & {
  /**
   * Narrowed to the three fields the video renderer reads, so a caller holding a
   * local blob URL (creative-import Review) can supply one without fabricating a
   * whole persisted `AdVideo`. A real `AdVideo` stays assignable.
   */
  singleVideo?: Pick<AdVideo, 'source' | 'storedSource' | 'thumbnail' | 'animatedImage'>
  /**
   * Narrowed the same way as `singleVideo`, and for the same reason: the
   * creative-import Review holds blob URLs for slides that have no persisted
   * AdImage/AdVideo behind them yet. A real `AdCreativeCarouselSlide` stays
   * assignable.
   */
  carouselSlides?: Array<{
    linkHeadline?: string
    linkDescription?: string
    linkWebsiteURL?: string
    mediaType?: CreativeMediaType
    image?: Pick<AdImage, 'url'>
    video?: Pick<AdVideo, 'source' | 'storedSource' | 'thumbnail' | 'animatedImage'>
  }>
  /** Per-placement only, narrowed the same way: the media each placement shows. */
  placementCuts?: Partial<
    Record<
      PlacementBucket,
      | { mediaType: 'image'; image: Pick<AdImage, 'url'> }
      | {
          mediaType: 'video'
          video: Pick<AdVideo, 'source' | 'storedSource' | 'thumbnail' | 'animatedImage'>
        }
    >
  >
}

/**
 * A per-placement ad draws as the single ad its placement's cut makes. A missing
 * cut draws as an empty single ad on purpose: looking wrong is the information.
 */
function forPlacement(
  creative: LocalAdPreviewCreative,
  placement: LocalAdPreviewPlacement
): LocalAdPreviewCreative {
  if (creative.format !== AdCreativeType.per_placement) return creative
  const cut =
    creative.placementCuts?.[placement === 'story' ? PlacementBucket.story : PlacementBucket.feed]
  const unset = { singleImage: undefined, imageURL: undefined, thumbnailURL: undefined }
  return cut?.mediaType === 'video'
    ? { ...creative, ...unset, format: AdCreativeType.single_video, singleVideo: cut.video }
    : {
        ...creative,
        ...unset,
        format: AdCreativeType.single_image,
        singleVideo: undefined,
        imageURL: cut?.image.url,
      }
}

/**
 * How the media is sized.
 *
 * - `cover` (default) — a square, centre-cropped box. What the live-iframe overlay
 *   in `MobilePreview` is sized against; do not change it for callers that stack
 *   this beneath the iframe.
 * - `feed` — what Meta's mobile feed does: the asset's own aspect ratio between
 *   1.91:1 and 4:5, centre-cropped only beyond that range. A 4:5 shows whole; a
 *   9:16 is cut to 4:5. For surfaces that draw the ad themselves and must match.
 */
export type LocalAdPreviewMediaFit = 'cover' | 'feed'

/**
 * Which Meta surface to imitate.
 *
 * - `feed` (default) — the Facebook feed card.
 * - `story` — the full-screen Instagram / Facebook / Messenger Story: a 9:16 frame
 *   at the caller's width with the media as its background, the page identity
 *   floated over the top and the call to action pinned to the bottom. Stories
 *   draw no copy block, so primary text is not rendered. `mediaFit` does not apply.
 */
export type LocalAdPreviewPlacement = 'feed' | 'story'

export interface LocalAdPreviewProps {
  creative: LocalAdPreviewCreative
  /** Facebook page name shown in the header; falls back to a neutral label. */
  pageName?: string
  /** Facebook page avatar; falls back to an initials chip when absent. */
  pageImageURL?: string
  /** Media sizing. Defaults to `cover` — the square feed crop. */
  mediaFit?: LocalAdPreviewMediaFit
  /**
   * Render the page name / avatar / "Sponsored" header. Defaults to `true`.
   * Callers without a real page (import review) should pass `false` rather than
   * show a placeholder identity, which reads as broken.
   */
  showPageHeader?: boolean
  /** Surface to draw. Defaults to `feed`. */
  placement?: LocalAdPreviewPlacement
}

/**
 * Formats `CreativePreview` will show this renderer for while Facebook's own is
 * still loading. Excluding a format means its consumers see a spinner instead,
 * so this is about what is worth showing mid-load — not about approval, and not
 * about what this component can draw (carousel draws a real card row).
 *
 * The sprint packet's review surface does not consult this: it renders here
 * directly and never fetches Facebook.
 */
export const isLocallyRenderable = (format: AdCreativeType) =>
  format === AdCreativeType.single_image || format === AdCreativeType.single_video

/** FB feed truncates the primary text and shows "… See more"; ~125 chars is the rule of thumb. */
const PRIMARY_TEXT_LIMIT = 125

/** Keeps `feed` placeholders from collapsing to nothing without imposing a ratio. */
const FEED_PLACEHOLDER_MIN_HEIGHT = 200

const colors = {
  card: '#ffffff',
  border: '#dadde1',
  ink: '#050505',
  muted: '#65676b',
  faint: '#8a8d91',
  linkStrip: '#f0f2f5',
  ctaBg: '#e4e6eb',
  ctaInk: '#050505',
  avatarBg: '#c4cdd6',
  storyFrame: '#242526',
  storyInk: '#ffffff',
  storyMuted: '#b0b3b8',
  storyCtaBg: '#ffffff',
  storyCtaInk: '#050505',
}

const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'

function mediaStyle(fit: LocalAdPreviewMediaFit): React.CSSProperties {
  if (fit === 'feed') {
    // `cqw` is a percentage of the card's width (the card is the size container),
    // which states the 4:5 and 1.91:1 limits without knowing the asset's size.
    return {
      display: 'block',
      width: '100%',
      height: 'auto',
      maxHeight: '125cqw',
      minHeight: `${100 / 1.91}cqw`,
      objectFit: 'cover',
    }
  }
  return { width: '100%', display: 'block', aspectRatio: '1 / 1', objectFit: 'cover' }
}

function placeholderStyle(fit: LocalAdPreviewMediaFit): React.CSSProperties {
  return {
    width: '100%',
    ...(fit === 'feed' ? { minHeight: FEED_PLACEHOLDER_MIN_HEIGHT } : { aspectRatio: '1 / 1' }),
    background: colors.linkStrip,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.faint,
    fontSize: 13,
  }
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('')
}

function PageHeader({ pageName, pageImageURL }: { pageName: string; pageImageURL?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 12px 8px' }}>
      {pageImageURL ? (
        <img
          src={pageImageURL}
          alt=""
          style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <div
          aria-hidden
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: colors.avatarBg,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 15,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {initials(pageName)}
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: colors.ink, lineHeight: 1.2 }}>
          {pageName}
        </div>
        <div style={{ fontSize: 12, color: colors.faint, lineHeight: 1.4 }}>Sponsored · 🌐</div>
      </div>
    </div>
  )
}

function PrimaryText({ text, padTop }: { text: string; padTop?: boolean }) {
  const [expanded, setExpanded] = React.useState(false)
  const truncatable = text.length > PRIMARY_TEXT_LIMIT
  const shown = truncatable && !expanded ? `${text.slice(0, PRIMARY_TEXT_LIMIT).trimEnd()}…` : text
  return (
    <div
      style={{
        // The page header normally supplies the top gap; pad here when it's hidden.
        padding: padTop ? '16px 14px 12px' : '0 12px 10px',
        fontSize: 14,
        lineHeight: 1.4,
        color: colors.ink,
        whiteSpace: 'pre-wrap',
      }}
    >
      {shown}
      {truncatable && (
        <>
          {' '}
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              font: 'inherit',
              color: colors.muted,
              cursor: 'pointer',
            }}
          >
            {expanded ? 'See less' : 'See more'}
          </button>
        </>
      )}
    </div>
  )
}

function ImageMedia({ url, fit }: { url?: string; fit: LocalAdPreviewMediaFit }) {
  if (!url) {
    return <div style={placeholderStyle(fit)}>No image</div>
  }
  return <img src={url} alt="" style={mediaStyle(fit)} />
}

function VideoUnavailable({ fit }: { fit: LocalAdPreviewMediaFit }) {
  return <div style={placeholderStyle(fit)}>Video unavailable</div>
}

/**
 * Native single-video playback for review. Prefers our durable stored copy
 * (`storedSource`) over the expiring Facebook `source`. If the video fails to
 * load, falls back to the poster image, then to a clear unavailable state — never
 * a broken <video> or <img>.
 */
function VideoMedia({
  source,
  poster,
  fit,
}: {
  source?: string
  poster?: string
  fit: LocalAdPreviewMediaFit
}) {
  const [errored, setErrored] = React.useState(false)

  if (source && !errored) {
    return (
      <video
        src={source}
        poster={poster}
        controls
        playsInline
        preload="metadata"
        onError={() => setErrored(true)}
        style={{ ...mediaStyle(fit), background: '#000' }}
      />
    )
  }
  if (poster) {
    return <ImageMedia url={poster} fit={fit} />
  }
  return <VideoUnavailable fit={fit} />
}

/** The feed's action button. Inert: this is a rendering of an ad, not the ad. */
function CtaButton({
  callToAction,
  compact,
}: {
  callToAction?: LocalAdPreviewCreative['callToAction']
  /** Sized down for a carousel card, which is a fraction of the ad's width. */
  compact?: boolean
}) {
  if (!callToAction) return null
  return (
    <button
      type="button"
      disabled
      style={{
        flexShrink: 0,
        border: 'none',
        borderRadius: 6,
        background: colors.ctaBg,
        color: colors.ctaInk,
        fontSize: compact ? 12 : 14,
        fontWeight: 600,
        padding: compact ? '6px 8px' : '8px 12px',
        cursor: 'default',
        whiteSpace: 'nowrap',
      }}
    >
      {formatCallToAction(callToAction)}
    </button>
  )
}

function LinkStrip({ creative }: { creative: LocalAdPreviewCreative }) {
  const { linkDisplayURL, linkHeadline, linkDescription, callToAction } = creative
  if (!linkDisplayURL && !linkHeadline && !linkDescription && !callToAction) return null
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 12px',
        background: colors.linkStrip,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        {linkDisplayURL && (
          <div
            style={{
              fontSize: 12,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
              color: colors.faint,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {linkDisplayURL}
          </div>
        )}
        {linkHeadline && (
          <div style={{ fontSize: 15, fontWeight: 600, color: colors.ink, lineHeight: 1.3 }}>
            {linkHeadline}
          </div>
        )}
        {linkDescription && (
          <div style={{ fontSize: 13, color: colors.muted, lineHeight: 1.3 }}>
            {linkDescription}
          </div>
        )}
      </div>
      <CtaButton callToAction={callToAction} />
    </div>
  )
}

/**
 * The carousel's horizontally scrolling card row. Each card carries its own
 * media, headline, and description — which is the whole point of reviewing a
 * carousel locally: the per-slide copy is the part you can't check anywhere else
 * before the ad exists on Facebook.
 *
 * Cards are a fixed width and the row scrolls, matching how the feed shows them
 * (you see the first card and part of the second). The CTA is drawn on every
 * card because that is what publishing does: the ad's single call to action is
 * copied onto each child attachment, so the feed shows a button per card and no
 * button for the ad as a whole. Media is square-cropped even
 * in `feed` mode: unlike a single asset, a carousel's cards MUST agree on
 * shape or the row reads as broken, and Facebook crops them anyway.
 */
function CarouselMedia({
  slides,
  displayURL,
  callToAction,
}: {
  slides: NonNullable<LocalAdPreviewCreative['carouselSlides']>
  displayURL?: string
  callToAction?: LocalAdPreviewCreative['callToAction']
}) {
  return (
    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '0 12px 12px' }}>
      {slides.map((slide, index) => {
        const isVideoSlide = slide.mediaType === 'video'
        // A GIF card draws from our stored copy, which is the GIF itself and
        // animates in an `<img>`; `source` would be the transcoded mp4.
        const source = isVideoSlide
          ? slide.video?.animatedImage
            ? slide.video.storedSource ?? slide.video.thumbnail
            : slide.video?.thumbnail
          : slide.image?.url
        // No Facebook thumbnail yet: show a frame of the video itself rather
        // than the word "Video". The row is `<img>`-shaped by design, so this
        // is a still, not playback — the ad's own preview above is where a
        // reviewer plays it.
        const stillFrame =
          !source && isVideoSlide && !slide.video?.animatedImage
            ? slide.video?.storedSource
            : undefined
        return (
          <div
            key={index}
            style={{
              flex: '0 0 auto',
              width: 200,
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              overflow: 'hidden',
              background: colors.card,
            }}
          >
            {source ? (
              <img
                src={source}
                alt=""
                style={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : stillFrame ? (
              <video
                src={videoStillURL(stillFrame)}
                muted
                playsInline
                preload="metadata"
                style={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  objectFit: 'cover',
                  display: 'block',
                  background: '#000',
                }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  background: colors.linkStrip,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.faint,
                  fontSize: 12,
                }}
              >
                {slide.mediaType === 'video' ? 'Video' : 'No media'}
              </div>
            )}
            <div
              style={{
                background: colors.linkStrip,
                padding: '8px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                {displayURL && (
                  <div
                    style={{
                      color: colors.faint,
                      fontSize: 11,
                      textTransform: 'uppercase',
                      marginBottom: 2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {displayURL}
                  </div>
                )}
                <div style={{ color: colors.ink, fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
                  {slide.linkHeadline || <span style={{ color: colors.faint }}>No headline</span>}
                </div>
                {slide.linkDescription && (
                  <div style={{ color: colors.muted, fontSize: 12, marginTop: 2 }}>
                    {slide.linkDescription}
                  </div>
                )}
              </div>
              <CtaButton callToAction={callToAction} compact />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function Placeholder() {
  return (
    <div
      style={{
        padding: '28px 16px',
        textAlign: 'center',
        color: colors.muted,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 600, color: colors.ink, marginBottom: 4 }}>Dynamic creative</div>
      Live Facebook preview unavailable — open in Facebook to review this format.
    </div>
  )
}

/** What a story frame draws behind its chrome: the first thing a viewer would see. */
type StoryMediaSource =
  | { kind: 'image'; url?: string }
  | { kind: 'video'; source?: string; poster?: string }

/**
 * Stories show one card at a time, so a carousel is drawn as its first card. GIFs
 * draw from the stored copy as an image, for the same reason as in the feed.
 */
function storyMediaSource(creative: LocalAdPreviewCreative): StoryMediaSource | undefined {
  switch (creative.format) {
    case AdCreativeType.single_image:
      return {
        kind: 'image',
        url: creative.singleImage?.url ?? creative.imageURL ?? creative.thumbnailURL,
      }
    case AdCreativeType.single_video: {
      const video = creative.singleVideo
      if (video?.animatedImage) return { kind: 'image', url: video.storedSource ?? video.thumbnail }
      return {
        kind: 'video',
        source: video?.storedSource ?? video?.source,
        poster: video?.thumbnail ?? creative.thumbnailURL,
      }
    }
    case AdCreativeType.carousel: {
      const slide = creative.carouselSlides?.[0]
      if (slide?.mediaType !== 'video') return { kind: 'image', url: slide?.image?.url }
      if (slide.video?.animatedImage) {
        return { kind: 'image', url: slide.video.storedSource ?? slide.video.thumbnail }
      }
      return {
        kind: 'video',
        source: slide.video?.storedSource ?? slide.video?.source,
        poster: slide.video?.thumbnail,
      }
    }
    default:
      return undefined
  }
}

const storyLayerStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  display: 'block',
}

/**
 * Deliberately unflattering: media that isn't 9:16 is scaled to the frame's width
 * and centred, with a blurred, enlarged copy of itself filling the rest — what
 * Meta does. Cropping a square to fill the frame would look better and lie in
 * exactly the case this preview exists to catch. Already-9:16 media covers the
 * backdrop entirely, so it reads as full-bleed without measuring anything.
 */
const storyForegroundStyle: React.CSSProperties = { ...storyLayerStyle, objectFit: 'contain' }

const storyBackdropStyle: React.CSSProperties = {
  ...storyLayerStyle,
  objectFit: 'cover',
  filter: 'blur(24px) brightness(0.8)',
  transform: 'scale(1.2)',
}

function StoryEmpty({ label }: { label: string }) {
  return (
    <div
      style={{
        ...storyLayerStyle,
        background: colors.storyFrame,
        color: colors.storyMuted,
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {label}
    </div>
  )
}

function StoryImage({ url }: { url?: string }) {
  if (!url) return <StoryEmpty label="No media yet" />
  return (
    <>
      <img src={url} alt="" aria-hidden style={storyBackdropStyle} />
      <img src={url} alt="" style={storyForegroundStyle} />
    </>
  )
}

/** Same fallback chain as the feed's `VideoMedia`: source, then poster, then unavailable. */
function StoryVideo({ source, poster }: { source?: string; poster?: string }) {
  const [errored, setErrored] = React.useState(false)

  if (source && !errored) {
    return (
      <>
        {poster ? (
          <img src={poster} alt="" aria-hidden style={storyBackdropStyle} />
        ) : (
          <video
            src={videoStillURL(source)}
            aria-hidden
            muted
            playsInline
            preload="metadata"
            style={storyBackdropStyle}
          />
        )}
        <video
          src={source}
          poster={poster}
          controls
          playsInline
          preload="metadata"
          onError={() => setErrored(true)}
          style={storyForegroundStyle}
        />
      </>
    )
  }
  if (poster) return <StoryImage url={poster} />
  return <StoryEmpty label="Video unavailable" />
}

/** Stories' segmented progress bar: one segment per card, the current one lit. */
function StoryProgress({ segments }: { segments: number }) {
  return (
    <div style={{ display: 'flex', gap: 3, marginBottom: 8 }}>
      {Array.from({ length: segments }, (_, index) => (
        <div
          key={index}
          style={{
            flex: 1,
            height: 2,
            borderRadius: 1,
            background: colors.storyInk,
            opacity: index === 0 ? 0.95 : 0.4,
          }}
        />
      ))}
    </div>
  )
}

function StoryHeader({ pageName, pageImageURL }: { pageName: string; pageImageURL?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {pageImageURL ? (
        <img
          src={pageImageURL}
          alt=""
          style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <div
          aria-hidden
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: colors.avatarBg,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {initials(pageName)}
        </div>
      )}
      <div style={{ minWidth: 0, lineHeight: 1.25 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: colors.storyInk }}>{pageName}</div>
        <div style={{ fontSize: 11, color: colors.storyInk, opacity: 0.85 }}>Sponsored</div>
      </div>
    </div>
  )
}

/** The swipe-up pill Stories pin to the bottom. Inert, like the feed's button. */
function StoryCta({ callToAction }: { callToAction?: LocalAdPreviewCreative['callToAction'] }) {
  if (!callToAction) return null
  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <svg aria-hidden width="16" height="8" viewBox="0 0 16 8" fill="none">
        <path d="M1 7l7-6 7 6" stroke={colors.storyInk} strokeWidth="2" strokeLinecap="round" />
      </svg>
      <button
        type="button"
        disabled
        style={{
          border: 'none',
          borderRadius: 999,
          background: colors.storyCtaBg,
          color: colors.storyCtaInk,
          fontSize: 14,
          fontWeight: 600,
          padding: '8px 18px',
          cursor: 'default',
          whiteSpace: 'nowrap',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
        }}
      >
        {formatCallToAction(callToAction)}
      </button>
    </div>
  )
}

function StoryAd({
  creative,
  pageName,
  pageImageURL,
  showPageHeader,
}: {
  creative: LocalAdPreviewCreative
  pageName: string
  pageImageURL?: string
  showPageHeader: boolean
}) {
  const media = storyMediaSource(creative)
  const isCarousel = creative.format === AdCreativeType.carousel
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '9 / 16',
        background: colors.storyFrame,
        borderRadius: 8,
        overflow: 'hidden',
        fontFamily: FONT_FAMILY,
        textAlign: 'left',
      }}
    >
      {media?.kind === 'image' && <StoryImage url={media.url} />}
      {media?.kind === 'video' && (
        <StoryVideo key={media.source} source={media.source} poster={media.poster} />
      )}
      {!media && <StoryEmpty label="Dynamic creative" />}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          padding: '8px 10px 24px',
          // Keeps white chrome legible over light media, as Stories do.
          background: 'linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0))',
          pointerEvents: 'none',
        }}
      >
        <StoryProgress
          segments={isCarousel ? Math.max(creative.carouselSlides?.length ?? 0, 1) : 1}
        />
        {showPageHeader && <StoryHeader pageName={pageName} pageImageURL={pageImageURL} />}
      </div>
      {media && <StoryCta callToAction={creative.callToAction} />}
    </div>
  )
}

/**
 * A Facebook-independent, network-free approximation of the FB mobile-feed ad,
 * drawn entirely from fields already hydrated on the creative (no preview fetch).
 * Used as the instant base layer beneath the live FB iframe (see `MobilePreview`'s
 * `fallback`): it paints immediately and stays visible whenever the live FB
 * preview is slow, down, or token-expired, so an AE can still review and approve
 * the ad's copy, image, headline, display URL, and CTA.
 *
 * Faithful for `single_image` / `single_video`, and a real card row for `carousel`.
 * `placement="story"` draws the same creative as a full-screen Story instead.
 * `dynamic` renders a minimal informative placeholder rather than a faked layout —
 * callers wanting a real dynamic render must pin a combination and pass that.
 */
export function LocalAdPreview({
  creative: given,
  pageName,
  pageImageURL,
  mediaFit = 'cover',
  showPageHeader = true,
  placement = 'feed',
}: LocalAdPreviewProps) {
  const creative = forPlacement(given, placement)
  const name = pageName?.trim() || 'Your Page'
  if (placement === 'story') {
    return (
      <StoryAd
        creative={creative}
        pageName={name}
        pageImageURL={pageImageURL}
        showPageHeader={showPageHeader}
      />
    )
  }
  const isImage = creative.format === AdCreativeType.single_image
  const isVideo = creative.format === AdCreativeType.single_video
  const isCarousel = creative.format === AdCreativeType.carousel

  return (
    <div
      style={{
        width: '100%',
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        overflow: 'hidden',
        fontFamily: FONT_FAMILY,
        textAlign: 'left',
        containerType: 'inline-size',
      }}
    >
      {showPageHeader && <PageHeader pageName={name} pageImageURL={pageImageURL} />}
      {creative.primaryText && <PrimaryText text={creative.primaryText} padTop={!showPageHeader} />}
      {isImage && (
        <ImageMedia
          url={creative.singleImage?.url ?? creative.imageURL ?? creative.thumbnailURL}
          fit={mediaFit}
        />
      )}
      {/* A GIF is a single_video ad — Facebook ingests it that way — but it has
        to be drawn as an image, and from our stored copy rather than `source`,
        which becomes the transcoded mp4. */}
      {isVideo && creative.singleVideo?.animatedImage && (
        <ImageMedia
          url={creative.singleVideo.storedSource ?? creative.singleVideo.thumbnail}
          fit={mediaFit}
        />
      )}
      {isVideo && !creative.singleVideo?.animatedImage && (
        <VideoMedia
          key={creative.singleVideo?.storedSource ?? creative.singleVideo?.source}
          source={creative.singleVideo?.storedSource ?? creative.singleVideo?.source}
          poster={creative.singleVideo?.thumbnail ?? creative.thumbnailURL}
          fit={mediaFit}
        />
      )}
      {isCarousel && (
        <CarouselMedia
          slides={creative.carouselSlides ?? []}
          displayURL={creative.linkDisplayURL}
          callToAction={creative.callToAction}
        />
      )}
      {(isImage || isVideo) && <LinkStrip creative={creative} />}
      {!isImage && !isVideo && !isCarousel && <Placeholder />}
    </div>
  )
}
